import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Notiflix from 'notiflix';
import PageMeta from '../components/SEO/PageMeta';
import StructuredData from '../components/SEO/StructuredData';

const Contact = () => {
    const { t } = useTranslation();

    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [company, setCompany] = useState('');
    const [message, setMessage] = useState('');
    const [errors, setErrors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const clearErrors = () => setErrors([]);
    const clearInput = () => {
        setFirstName('');
        setLastName('');
        setEmail('');
        setCompany('');
        setMessage('');
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsSubmitting(true);

        let fData = new FormData();
        fData.append('first_name', firstName);
        fData.append('last_name', lastName);
        fData.append('email', email);
        fData.append('company', company);
        fData.append('message', message);

        axios({
            method: 'post',
            url: process.env.REACT_APP_CONTACT_API,
            data: fData,
            headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then(function (response) {
            clearInput();
            Notiflix.Report.success(
                t('contact.notifications.success_title'),
                response.data.message || t('contact.notifications.success_message'),
                'Okay'
            );
        })
        .catch(function (error) {
            const { response } = error;
            if (response && response.data && response.data.errors) {
                setErrors(response.data.errors);
            } else {
                 Notiflix.Report.failure(
                    t('contact.notifications.failure_title'),
                    t('contact.notifications.failure_message'),
                    'Okay'
                );
            }
        })
        .finally(() => {
            setIsSubmitting(false);
        });
    };

    return (
        <>
            <PageMeta
                title={t('contact.doc_title')}
                description={t('contact.meta_description')}
                path="/contact"
            />
            <StructuredData type="page" page={{ name: t('nav.contact'), path: '/contact', kind: 'ContactPage', description: t('contact.meta_description') }} />

            <div className="bg-white py-12" id="contact">
                <div className="max-w-7xl mx-auto px-4 pt-8">
                    
                    <div className="text-center mb-16" data-aos="fade-down">
                        <h1 className="text-3xl text-primary uppercase font-bold">{t('contact.title')}</h1>
                        <div className='flex justify-center mt-2'>
                            <div className='w-24 border-b-4 border-primary'></div>
                        </div>
                        <p className="mt-4 text-center text-xl text-gray-600 max-w-3xl mx-auto">{t('contact.subtitle')}</p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-16 items-start">
                        
                        <div className="bg-gray-50 p-8 rounded-2xl shadow-lg" data-aos="fade-right">
                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label htmlFor="first_name" className="block text-lg font-medium text-gray-700">{t('contact.form.first_name_label')}</label>
                                        <input type="text" name="first_name" id="first_name" required value={firstName} onChange={e => setFirstName(e.target.value)} onKeyUp={clearErrors} className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"/>
                                        {errors.first_name && <p className="text-red-500 text-sm mt-1">{errors.first_name}</p>}
                                    </div>
                                    <div>
                                        <label htmlFor="last_name" className="block text-lg font-medium text-gray-700">{t('contact.form.last_name_label')}</label>
                                        <input type="text" name="last_name" id="last_name" required value={lastName} onChange={e => setLastName(e.target.value)} onKeyUp={clearErrors} className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"/>
                                        {errors.last_name && <p className="text-red-500 text-sm mt-1">{errors.last_name}</p>}
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="email" className="block text-lg font-medium text-gray-700">{t('contact.form.email_label')}</label>
                                    <input type="email" name="email" id="email" required value={email} onChange={e => setEmail(e.target.value)} onKeyUp={clearErrors} className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"/>
                                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                                </div>
                                <div>
                                    <label htmlFor="company" className="block text-lg font-medium text-gray-700">{t('contact.form.company_label')}</label>
                                    <input type="text" name="company" id="company" value={company} onChange={e => setCompany(e.target.value)} onKeyUp={clearErrors} className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"/>
                                </div>
                                <div>
                                    <label htmlFor="message" className="block text-lg font-medium text-gray-700">{t('contact.form.message_label')}</label>
                                    <textarea name="message" id="message" rows="5" required value={message} onChange={e => setMessage(e.target.value)} onKeyUp={clearErrors} placeholder={t('contact.form.message_placeholder')} className="mt-1 block w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-primary focus:border-primary"></textarea>
                                    {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
                                </div>
                                <div className="text-center">
                                    <button type="submit" disabled={isSubmitting} className="text-white bg-primary hover:bg-primary-hover inline-flex items-center justify-center w-full px-8 py-4 my-4 text-lg shadow-xl rounded-xl sm:w-auto disabled:opacity-75">
                                        {isSubmitting ? t('contact.form.loading_button') : t('contact.form.submit_button')}
                                    </button>
                                </div>
                            </form>
                        </div>

                        <div className="space-y-8" data-aos="fade-left">
                             <div>
                                <h2 className="text-2xl font-bold text-primary mb-2">{t('footer.company_info.address_label')}</h2>
                                <p className="text-lg text-gray-600">{t('footer.company_info.address_value')}</p>
                            </div>
                             <div>
                                <h2 className="text-2xl font-bold text-primary mb-2">{t('contact.sidebar.email_label')}</h2>
                                <a href={`mailto:${t('footer.company_info.email')}`} className="text-lg text-gray-600 hover:text-primary">{t('footer.company_info.email')}</a>
                            </div>
                            <div>
                               <div className="aspect-w-16 aspect-h-9 rounded-2xl overflow-hidden shadow-lg">
                                    <iframe 
                                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2690.669824647355!2d19.14129307688224!3d47.59253918855904!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4741c50305141c2d%3A0x19a557b7f5255866!2sBudapest%2C%20V%C3%A1roskapu%20u.%2014%2C%201152!5e0!3m2!1sen!2shu!4v1718556942971!5m2!1sen!2shu"
                                        width="100%" 
                                        height="350" 
                                        style={{ border: 0 }} 
                                        allowFullScreen="" 
                                        loading="lazy" 
                                        referrerPolicy="no-referrer-when-downgrade"
                                        title="ROBOMIND Kft. AI Lab Location"
                                    ></iframe>
                               </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Contact;